import { SearchBoxChangeEvent, Spinner } from "@fluentui/react-components";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { LayoutContext } from "../../context/LayoutContext";
import { useMultiAirportDateRanges } from "../../hooks/useMultiAirportDateRanges";
import { useMultiAirportOperationalData } from "../../hooks/useMultiAirportOperationalData";
import {
  AirportKpiErrors,
  MultiAirportDateRangeFailure,
  MultiAirportOperationalDataFailure,
} from "../../models/airports/AirportConfig";
import { addTimeZoneDifference } from "../../utils/common/CommonHelperFunctions";
import { EMPTY_STATE_MESSAGE } from "../../utils/common/Constants";
import {
  calculateAirportStatusCount,
  extractAirportMapDetails,
  filterAirportsByStatus,
  findMatches,
  getCommonAirports,
} from "../../utils/multi-airports/MultiAirportHelperFunctions";
import { NoDataCard } from "../common/no-data-card/NoDataCard";
import MapDisplayAirports from "../osm-maps/map-displays/MapDisplayAirports";
import AirportList from "./AirportList";
import { AirportPerformance } from "./AirportPerformance";
import MultiAirportSidebar from "./MultiAirportSidebar";

interface IMultiAirportProps {
  airportConfiguration: any[];
}

const getAirportCode = (airport: any): string =>
  (airport.iataCode || airport.code || airport.id || "").trim().toUpperCase();

const hasTargetError = <T extends { airportCode: string; target: string }>(
  errors: T[],
  airportCode: string,
  targets: string[]
): boolean =>
  errors.some(
    (error) =>
      error.airportCode.trim().toUpperCase() === airportCode &&
      targets.includes(error.target)
  );

const getAirportKpiErrors = (
  airportCode: string,
  dateRangeErrors: MultiAirportDateRangeFailure[],
  operationalDataErrors: MultiAirportOperationalDataFailure[]
): AirportKpiErrors => ({
  otp:
    hasTargetError(dateRangeErrors, airportCode, ["otp", "all"]) ||
    hasTargetError(operationalDataErrors, airportCode, ["otp", "all"]),
  flights:
    hasTargetError(dateRangeErrors, airportCode, ["flights", "all"]) ||
    hasTargetError(operationalDataErrors, airportCode, ["flights", "all"]),
  notifications: hasTargetError(operationalDataErrors, airportCode, [
    "notifications",
    "all",
  ]),
});

export const MultiAirport: React.FunctionComponent<IMultiAirportProps> = ({
  airportConfiguration,
}) => {
  const [airportList, setAirportList] = useState<any[]>([]);
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>("all");
  const [airportSearch, setAirportSearch] = useState<any[]>([]);
  const { isMapView } = useContext(LayoutContext);
  const [sideBarActive, setSideBarActive] = useState<boolean>(true);
  const [time, setTime] = useState<Date>(new Date());
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);

  const {
    payload: dateRangePayload,
    errors: dateRangeErrors,
    isLoading: areDateRangesLoading,
    isLoaded: areDateRangesLoaded,
  } = useMultiAirportDateRanges(airportConfiguration, time);

  const timeZoneAwareAirports = useMemo(() => {
    if (!airportConfiguration || airportConfiguration.length === 0) {
      return [];
    }

    return addTimeZoneDifference(
      airportConfiguration.map((airport: any) => ({
        ...airport,
        isPinned: false,
      }))
    );
  }, [airportConfiguration]);

  const {
    airports: enrichedAirports,
    errors: operationalDataErrors,
    isLoading: isOperationalDataLoading,
  } = useMultiAirportOperationalData(
    timeZoneAwareAirports,
    dateRangePayload,
    areDateRangesLoaded
  );

  const hasAirportConfiguration = timeZoneAwareAirports.length > 0;
  const isLoading = hasAirportConfiguration && !hasCompletedInitialLoad;

  useEffect(() => {
    if (
      !hasCompletedInitialLoad &&
      areDateRangesLoaded &&
      !areDateRangesLoading &&
      !isOperationalDataLoading &&
      enrichedAirports.length > 0
    ) {
      setHasCompletedInitialLoad(true);
    }
  }, [
    areDateRangesLoaded,
    areDateRangesLoading,
    enrichedAirports.length,
    hasCompletedInitialLoad,
    isOperationalDataLoading,
  ]);

  const airportsWithKpiErrors = useMemo(
    () =>
      enrichedAirports.map((airport) => {
        const airportCode = getAirportCode(airport);
        return {
          ...airport,
          kpiErrors: getAirportKpiErrors(
            airportCode,
            dateRangeErrors,
            operationalDataErrors
          ),
        };
      }),
    [dateRangeErrors, enrichedAirports, operationalDataErrors]
  );

  const haveAllApisFailed =
    airportsWithKpiErrors.length > 0 &&
    airportsWithKpiErrors.every(
      ({ kpiErrors }) =>
        kpiErrors.otp && kpiErrors.flights && kpiErrors.notifications
    );

  const showGlobalNoData =
    !isLoading && (!hasAirportConfiguration || haveAllApisFailed);

  useEffect(() => {
    if (dateRangeErrors.length > 0) {
      console.error("Multi-airport KPI date range errors:", dateRangeErrors);
    }
    if (operationalDataErrors.length > 0) {
      console.error(
        "Multi-airport operational data errors:",
        operationalDataErrors
      );
    }
  }, [dateRangeErrors, operationalDataErrors]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  // Filter logic
  const filterAirportList = (tagStatusType: string) => {
    setActiveStatusFilter(tagStatusType);
  };

  useEffect(() => {
    setAirportList(airportsWithKpiErrors);
    setAirportSearch([]);
  }, [airportsWithKpiErrors]);

  const updatePinnedStatus = (iataCode: string) => {
    setAirportList((currentAirportList) =>
      currentAirportList.map((airport: any) =>
        airport.iataCode === iataCode
          ? { ...airport, isPinned: !airport.isPinned }
          : airport
      )
    );
  };

  const filterData = useMemo(
    () => filterAirportsByStatus(airportList, activeStatusFilter),
    [activeStatusFilter, airportList]
  );

  // Search
  function searchInputHandler(event: SearchBoxChangeEvent): void {
    const { value } = event.target as HTMLInputElement;
    const dataToSearch = filterData.length ? filterData : airportList; // search only within filtered data
    const matchedAirports = findMatches(value, dataToSearch);
    setAirportSearch(matchedAirports);
  }

  // Derived data
  const airports = airportSearch?.length ? airportSearch : filterData;
  const airportStatusCount = calculateAirportStatusCount(airportList);
  const commonAirports = getCommonAirports(airports, filterData);
  const airportMapDetails = extractAirportMapDetails(commonAirports);

  return (
    <div data-testid="multi-airport">
      {isLoading && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          style={{ height: "80vh" }}
        />
      )}

      {showGlobalNoData && (
        <div style={{ display: "flex", height: "80vh" }}>
          <NoDataCard message={EMPTY_STATE_MESSAGE.Data} marginAll />
        </div>
      )}

      {!isLoading && !showGlobalNoData && (
        <>
          <AirportPerformance
            filterAirport={filterAirportList}
            setAirportSearch={setAirportSearch}
            airportStatusCount={airportStatusCount}
            sideBarActive={sideBarActive}
            searchInputHandler={searchInputHandler}
          />

          {isMapView && (
            <MapDisplayAirports airportMapDetails={airportMapDetails} />
          )}

          {isMapView ? (
            <MultiAirportSidebar
              airportList={commonAirports}
              onPinClickHandler={updatePinnedStatus}
              currentTime={time}
              sideBarActive={sideBarActive}
              setSideBarActive={setSideBarActive}
              searchInputHandler={searchInputHandler}
            />
          ) : (
            <AirportList
              airportList={commonAirports}
              onPinClickHandler={updatePinnedStatus}
              currentTime={time}
            />
          )}
        </>
      )}
    </div>
  );
};
